// Copyright (c) StrangeBee 2023

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import React, { ReactElement, useMemo } from 'react';
import { CaseOutput } from 'thehive-sdk';

import './CaseBulkDelete.scss';

interface CaseBulkDeleteValuesForm {
    casesCount: number;
}

interface CaseBulkDeleteProps {
    triggerElem: ReactElement;
    selectedCases: CaseOutput[];
    onMultipleDelete: (caseIds: string[]) => void;
}

export const CaseBulkDelete = ({ triggerElem, selectedCases, onMultipleDelete }: CaseBulkDeleteProps): ReactElement => {
    const i = useLocalMessage();

    const casesNumber = useMemo(() => selectedCases.length, [selectedCases]);

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = ({ casesCount = 0 }: CaseBulkDeleteValuesForm) => {
        if (casesCount === casesNumber) {
            onMultipleDelete(selectedCases.map(({ _id }) => _id));
        }
    };

    return (
        <THDrawer
            title={i('case.delete.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <THForm<CaseBulkDeleteValuesForm>
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(values) => {
                        toggleDrawer();
                        onFinish(values);
                        setFieldsTouched(false);
                    }}
                    className="case-bulk-delete"
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                    toggleDrawer();
                                }}
                                disabled={!isValid || !fieldsTouched}
                                footerText={i('case.delete.footer')}
                            />
                        }
                    >
                        <div className="case-bulk-delete__description">
                            <div className="case-bulk-delete__question">
                                {i('case.delete.question', { casesNumber })}
                            </div>
                            <div className="case-bulk-delete__list">
                                {selectedCases.map(({ _id, number, title }) => (
                                    <div key={_id}>{`#${number} - ${title}`}</div>
                                ))}
                            </div>
                        </div>

                        <THForm.Item
                            label={i('case.delete.confirm')}
                            name="casesCount"
                            className="case-bulk-delete__input-number"
                            rules={[
                                {
                                    required: true,
                                    validator: (_, value) => {
                                        if (value !== casesNumber) {
                                            return Promise.reject(i('case.delete.error'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <THInputNumber placeholder={casesNumber + ''} />
                        </THForm.Item>

                        <div className="case-bulk-delete__warning">{i('case.delete.warning')}</div>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
